import { useEffect, useState } from "react"
import Modal from "Components/molecule/modal"
import ActionButton from "Components/atoms/buttons/action-button"
import TextInput from "Components/molecule/inputs/text-input"
import MultiSelect from "Components/molecule/inputs/multi-select"
import { ExpenseBudgetVendor, VendorCodeOption, VendorPayload } from "models/expense-budget"
import { getExpenseBudgetVendorCodes } from "slices/finance/expense-budget/thunk"

interface VendorFormModalProps {
    isOpen: boolean
    vendor: ExpenseBudgetVendor | null
    isSaving: boolean
    onClose: () => void
    onSave: (payload: VendorPayload) => Promise<void>
}

// The Acumatica vendors link this vendor to its AP bills (Mapping tab → Bills & Adjustments).
// A vendor can have several, e.g. Microsoft billed through the resellers LONG VIEW and TTT.
const VendorFormModal = ({ isOpen, vendor, isSaving, onClose, onSave }: VendorFormModalProps) => {
    const [name, setName] = useState("")
    const [vendorCodes, setVendorCodes] = useState<string[]>([])
    const [codeOptions, setCodeOptions] = useState<VendorCodeOption[]>([])
    const [isLoadingCodes, setIsLoadingCodes] = useState(false)

    useEffect(() => {
        if (!isOpen) return
        setName(vendor?.name ?? "")
        setVendorCodes(vendor?.codes.map(code => code.vendorCode) ?? [])
        if (codeOptions.length > 0) return
        setIsLoadingCodes(true)
        getExpenseBudgetVendorCodes().then(result => {
            setCodeOptions(result ?? [])
            setIsLoadingCodes(false)
        })
    }, [isOpen, vendor])

    const options = codeOptions.map(v => ({ value: v.vendorCode, label: `${v.vendorName} (${v.vendorCode})` }))
    const canSave = !!name.trim() && !isSaving
    const save = () => onSave({ name: name.trim(), vendorCodes })

    // Picking the first Acumatica vendor fills an empty name with its Acumatica name.
    const handleChangeCodes = (selected: string[]) => {
        setVendorCodes(selected)
        const first = codeOptions.find(v => v.vendorCode === selected[0])
        if (first && !name.trim()) setName(first.vendorName)
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={vendor ? "Edit Vendor" : "New Vendor"}>
            <div className="d-flex flex-column gap-2">
                <TextInput label="Vendor name" value={name} autoFocus onChange={e => setName(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && canSave) save() }} />
                <MultiSelect label="Acumatica vendors" disabled={isLoadingCodes}
                    placeholder={isLoadingCodes ? "Loading vendors..." : "Type to search (optional)"}
                    options={options} value={options.filter(o => vendorCodes.includes(o.value))}
                    onChange={selected => handleChangeCodes(selected.map(o => o.value))} />
                <span className="text-muted small">
                    Links this vendor to its Acumatica bills and adjustments. Choose several if the vendor is billed through resellers.
                </span>
                <div className="d-flex justify-content-end gap-2">
                    <ActionButton onClick={onClose}>Cancel</ActionButton>
                    <ActionButton disabled={!canSave} onClick={save}>
                        {isSaving ? "Saving..." : "Save"}
                    </ActionButton>
                </div>
            </div>
        </Modal>
    )
}

export default VendorFormModal
